import React from 'react';
import LegalPageLayout, { type LegalSection } from './LegalPageLayout';
import { PAGE_TITLES } from '../navigation/pageTitle';

const PrivacyPolicy: React.FC = () => {
  const sections: LegalSection[] = [
    {
      id: 'who-we-are',
      title: '1. Who we are',
      content: (
        <div>
          <p className="privacy-body">
            <strong>LulPay Remittance Limited</strong><br />
            UK Mall, Ggaba Road, Kansanga, Kampala, Uganda
          </p>
          <p className="privacy-body">
            Email:{' '}
            <a href="mailto:support@lulpay.com" className="privacy-link">
              support@lulpay.com
            </a>
            <br />
            WhatsApp Support:{' '}
            <a href="https://wa.me/256709413576" className="privacy-link">
              +256 709 413576
            </a>
          </p>
        </div>
      ),
    },
    {
      id: 'beta-testing',
      title: '2. Current beta-testing status',
      content: (
        <div className="privacy-callout">
          <p className="privacy-body">
            LulPay is currently conducting a limited, non-production beta test of its technology platform.
          </p>
          <p className="privacy-body">
            During this phase, LulPay is used only to test and improve product features, user experience,
            identity-verification flows, security controls, communications, and customer-support processes.
          </p>
          <p className="privacy-body">
            <strong>
              The LulPay platform is not currently available for live financial transactions.
            </strong>{' '}
            During the beta-testing phase, LulPay does not accept, hold, transfer, exchange, pay out, or
            otherwise process customer funds through the platform.
          </p>
          <p className="privacy-body">
            Testing may involve users in the United States, United Kingdom, European Union, and Uganda.
            Participation in beta testing does not mean that live financial services are available in those
            locations.
          </p>
          <p className="privacy-body">
            Any future live services will be introduced only after LulPay has determined that the relevant
            product, operational, partner, security, and regulatory requirements have been satisfied.
          </p>
        </div>
      ),
    },
    {
      id: 'scope',
      title: '3. Scope of this policy',
      content: (
        <div>
          <p className="privacy-body">This Privacy Policy applies to personal data collected through:</p>
          <ul className="privacy-list">
            <li>the LulPay website;</li>
            <li>LulPay mobile applications;</li>
            <li>account registration and beta testing;</li>
            <li>identity-verification and compliance-related workflows;</li>
            <li>customer-support channels, including email and WhatsApp;</li>
            <li>communications through official LulPay channels.</li>
          </ul>
          <p className="privacy-body">
            This policy does not replace the privacy notices, terms, or policies of independent third-party
            service providers that may support identity verification, cloud hosting, messaging, analytics,
            email delivery, security, or future financial services.
          </p>
        </div>
      ),
    },
    {
      id: 'data-collected',
      title: '4. Personal data we may collect',
      content: (
        <div>
          <p className="privacy-body">
            Depending on the feature or testing flow you use, we may collect the following categories of
            personal data.
          </p>

          <h3 className="privacy-subheading">A. Account and contact information</h3>
          <p className="privacy-body">This may include:</p>
          <ul className="privacy-list">
            <li>full name;</li>
            <li>phone number;</li>
            <li>email address;</li>
            <li>date of birth;</li>
            <li>country of residence;</li>
            <li>nationality;</li>
            <li>residential or postal address;</li>
            <li>account credentials;</li>
            <li>account-status information;</li>
            <li>other information needed to create, test, secure, or support your account.</li>
          </ul>

          <h3 className="privacy-subheading">B. Identity and compliance information</h3>
          <p className="privacy-body">
            To test identity-verification, fraud-prevention, security, and compliance-related processes,
            we or approved providers may request:
          </p>
          <ul className="privacy-list">
            <li>passport, national ID, residence permit, refugee document, or other identity document;</li>
            <li>proof of address;</li>
            <li>selfie, liveness, or identity-verification information;</li>
            <li>tax-identification information where relevant;</li>
            <li>occupation, source-of-funds, or other compliance-related information;</li>
            <li>verification status, reference numbers, and outcomes.</li>
          </ul>
          <p className="privacy-body">
            Where identity documents are submitted through an approved secure third-party verification
            flow, LulPay does not retain copies of those submitted document files. We may, however,
            receive and retain relevant verification outcomes, status information, or references needed
            to support beta testing, security, compliance, and future service readiness.
          </p>

          <h3 className="privacy-subheading">C. Support and communication data</h3>
          <p className="privacy-body">This may include:</p>
          <ul className="privacy-list">
            <li>emails;</li>
            <li>WhatsApp messages;</li>
            <li>support tickets;</li>
            <li>support attachments;</li>
            <li>complaints, questions, feedback, and requests;</li>
            <li>records of support actions and outcomes.</li>
          </ul>

          <h3 className="privacy-subheading">D. Technical and usage information</h3>
          <p className="privacy-body">We may collect technical information such as:</p>
          <ul className="privacy-list">
            <li>device identifiers;</li>
            <li>IP address;</li>
            <li>device type, operating system, and application version;</li>
            <li>app error reports and crash data;</li>
            <li>diagnostic and security logs;</li>
            <li>audit logs for important actions;</li>
            <li>app-usage analytics.</li>
          </ul>
          <p className="privacy-body">
            Our website does not currently use advertising trackers or behavioural advertising tools. We
            will update this Privacy Policy before introducing material new tracking, advertising, or
            marketing technologies.
          </p>
        </div>
      ),
    },
    {
      id: 'how-we-use',
      title: '5. How we use personal data',
      content: (
        <div>
          <p className="privacy-body">We may use personal data to:</p>
          <ul className="privacy-list">
            <li>create and administer beta-testing accounts;</li>
            <li>provide, test, operate, maintain, improve, and secure the LulPay platform;</li>
            <li>verify identity and assess eligibility for testing or future service availability;</li>
            <li>test compliance, fraud-prevention, security, and support workflows;</li>
            <li>respond to support requests, feedback, and complaints;</li>
            <li>communicate account, verification, testing, security, or support updates;</li>
            <li>investigate suspected fraud, abuse, security incidents, or unlawful activity;</li>
            <li>maintain records, logs, and audit trails;</li>
            <li>protect users, LulPay, service providers, and the public;</li>
            <li>comply with applicable legal, regulatory, security, and record-keeping obligations.</li>
          </ul>
          <p className="privacy-body">
            We do not currently use personal data to send promotional marketing messages. Should we
            introduce marketing in the future, we will provide appropriate notice and obtain consent
            where required.
          </p>
        </div>
      ),
    },
    {
      id: 'lawful-basis',
      title: '6. Lawful reasons for processing',
      content: (
        <div>
          <p className="privacy-body">
            Where applicable law requires a lawful basis, we process personal data because:
          </p>
          <ul className="privacy-list">
            <li>you have asked to participate in beta testing or use our services;</li>
            <li>you have provided consent;</li>
            <li>processing is necessary to operate, secure, test, and support the platform;</li>
            <li>processing is necessary to prevent fraud, abuse, or security incidents;</li>
            <li>processing is necessary to comply with legal or regulatory obligations; or</li>
            <li>another lawful basis applies under applicable data-protection law.</li>
          </ul>
        </div>
      ),
    },
    {
      id: 'sharing',
      title: '7. How we share personal data',
      content: (
        <div>
          <p className="privacy-body">
            We may share personal data only where necessary and appropriate with:
          </p>
          <ul className="privacy-list">
            <li>approved identity-verification and compliance service providers;</li>
            <li>cloud-hosting and infrastructure providers;</li>
            <li>customer-support, communications, and messaging providers;</li>
            <li>email-delivery providers;</li>
            <li>analytics, error-monitoring, and security-service providers;</li>
            <li>professional advisers, auditors, insurers, or consultants;</li>
            <li>
              competent regulators, courts, law-enforcement agencies, or public authorities where required
              by law or necessary to protect rights, safety, or security;
            </li>
            <li>
              future licensed financial, payment, banking, mobile-money, or payout partners, only where a
              live service is introduced and such sharing is necessary.
            </li>
          </ul>
          <p className="privacy-body">
            Our service providers may process personal data on our behalf or as independent controllers,
            depending on the service and applicable law. Their own privacy notices may also apply.
          </p>
          <p className="privacy-body privacy-highlight">
            <strong>We do not sell personal data.</strong>
          </p>
        </div>
      ),
    },
    {
      id: 'international-transfers',
      title: '8. International data transfers',
      content: (
        <div>
          <p className="privacy-body">
            LulPay is based in Uganda. Some of our technology, identity-verification, hosting, messaging,
            analytics, email, and security providers may process or store data outside Uganda or outside
            the country where you live.
          </p>
          <p className="privacy-body">
            This may include processing related to beta participants in the United States, United Kingdom,
            European Union, and Uganda.
          </p>
          <p className="privacy-body">
            Where cross-border processing occurs, we take reasonable steps to ensure that it is carried
            out lawfully and with appropriate safeguards.
          </p>
        </div>
      ),
    },
    {
      id: 'retention',
      title: '9. Data retention',
      content: (
        <div>
          <p className="privacy-body">
            We keep personal data only for as long as reasonably necessary for the purposes described in
            this Privacy Policy, including to:
          </p>
          <ul className="privacy-list">
            <li>operate and support beta testing;</li>
            <li>maintain security, logs, and audit records;</li>
            <li>investigate fraud, abuse, or technical incidents;</li>
            <li>respond to complaints or disputes;</li>
            <li>comply with legal, regulatory, accounting, or record-keeping obligations;</li>
            <li>prepare for future service availability where permitted by law.</li>
          </ul>
          <p className="privacy-body">
            Retention periods vary depending on the type of data, the purpose for which it was collected,
            legal obligations, and operational requirements.
          </p>
          <p className="privacy-body">
            When personal data is no longer needed, we will delete, anonymise, or securely archive it in
            accordance with applicable law and our operational needs.
          </p>
        </div>
      ),
    },
    {
      id: 'security',
      title: '10. Security',
      content: (
        <div>
          <p className="privacy-body">
            We use reasonable technical and organisational safeguards designed to protect personal data,
            including:
          </p>
          <ul className="privacy-list">
            <li>encrypted transport using HTTPS/TLS;</li>
            <li>role-based staff access;</li>
            <li>
              access restricted to staff who need information for support, security, compliance, or
              operations;
            </li>
            <li>audit logs for important actions;</li>
            <li>security monitoring and incident-response processes;</li>
            <li>restrictions on access to systems containing personal data.</li>
          </ul>
          <p className="privacy-body">
            No system can guarantee absolute security. You are responsible for protecting your account
            credentials and should contact us promptly if you believe your account or information may have
            been compromised.
          </p>
        </div>
      ),
    },
    {
      id: 'whatsapp-safety',
      title: '11. WhatsApp and support safety',
      content: (
        <div>
          <p className="privacy-body">
            LulPay may provide support through WhatsApp, email, and other official channels.
          </p>
          <div className="privacy-warning">
            <p className="privacy-body" style={{ marginBottom: '8px' }}>
              <strong>For your protection, do not send any of the following through WhatsApp:</strong>
            </p>
            <ul className="privacy-list" style={{ marginBottom: 0 }}>
              <li>passport, national ID, refugee document, residence permit, or selfie;</li>
              <li>passwords, PINs, one-time codes, recovery phrases, or account credentials;</li>
              <li>bank-login details;</li>
              <li>debit-card or credit-card details;</li>
              <li>sensitive identity or financial documents.</li>
            </ul>
          </div>
          <p className="privacy-body">
            Where documents are required, LulPay will direct you to an appropriate secure verification or
            upload process.
          </p>
          <p className="privacy-body">
            Messages sent through WhatsApp or other support channels may be processed by third-party
            communications and support providers that help us deliver those services. Use only official
            LulPay support contacts and remain cautious of impersonation or scam messages.
          </p>
        </div>
      ),
    },
    {
      id: 'your-rights',
      title: '12. Your rights and choices',
      content: (
        <div>
          <p className="privacy-body">Subject to applicable law, you may have the right to:</p>
          <ul className="privacy-list">
            <li>request access to personal data we hold about you;</li>
            <li>request correction of inaccurate or incomplete data;</li>
            <li>
              request deletion of personal data, subject to legal, security, or record-retention
              requirements;
            </li>
            <li>withdraw consent where processing is based on consent;</li>
            <li>object to or request restriction of certain processing;</li>
            <li>request information about how we use or share your data;</li>
            <li>raise a complaint with a relevant data-protection authority.</li>
          </ul>
          <p className="privacy-body">
            To make a privacy request, email{' '}
            <a href="mailto:support@lulpay.com" className="privacy-link">
              support@lulpay.com
            </a>{' '}
            with the subject line: <strong>Privacy Request</strong>
          </p>
          <p className="privacy-body">
            We may need to verify your identity before acting on a request. We will respond within the
            period required by applicable law.
          </p>
        </div>
      ),
    },
    {
      id: 'data-deletion',
      title: '13. Data-deletion requests',
      content: (
        <div>
          <p className="privacy-body">
            In the LulPay app, open Profile → Delete Account. Verify the email code, enter your PIN,
            and confirm your request to close your account.
          </p>
          <p className="privacy-body">
            You can restore the same account during the next three calendar months using
            “Restore a closed account” on the sign-in screen, your account email, an email code,
            and your existing password. Your username, email, phone number, and WhatsApp number stay
            reserved during this period.
          </p>
          <p className="privacy-body">
            Recovery ends after three months. Your username, email, phone number, and WhatsApp
            number become available for new local registration after final closure processing.
          </p>
          <p className="privacy-body">
            At final closure, identity details move from your live account to a restricted archive.
            We retain this archive for five years from final closure, then delete it and the canonical
            identity-document images. Financial records and required evidence remain; identity details
            are removed from the live account. Historical financial records may retain information
            required for legal and regulatory obligations.
          </p>
          <p className="privacy-body">
            For help or a personal-data deletion request, email{' '}
            <a href="mailto:support@lulpay.com" className="privacy-link">support@lulpay.com</a>{' '}
            with the subject <strong>Data Deletion Request</strong>. Do not attach identity documents
            unless we direct you to a secure verification process.
          </p>
        </div>
      ),
    },
    {
      id: 'children',
      title: '14. Children and age requirement',
      content: (
        <div>
          <p className="privacy-body">
            LulPay is intended only for persons aged <strong>18 years or older</strong>.
          </p>
          <p className="privacy-body">
            We do not knowingly collect personal data from children. If you believe that a child has
            provided us with personal data, contact us at{' '}
            <a href="mailto:support@lulpay.com" className="privacy-link">
              support@lulpay.com
            </a>
            .
          </p>
        </div>
      ),
    },
    {
      id: 'changes',
      title: '15. Changes to this Privacy Policy',
      content: (
        <div>
          <p className="privacy-body">
            We may update this Privacy Policy as our beta testing, products, partners, security
            practices, or legal obligations change.
          </p>
          <p className="privacy-body">
            When we make material changes, we will update the "Last updated" date above and, where
            appropriate, provide additional notice through our website, mobile application, or official
            support channels.
          </p>
        </div>
      ),
    },
    {
      id: 'contact',
      title: '16. Contact us',
      content: (
        <div>
          <p className="privacy-body">
            For privacy questions, requests, or complaints, contact:
          </p>
          <div className="privacy-contact-card">
            <p className="privacy-body" style={{ marginBottom: '6px' }}>
              <strong>LulPay Remittance Limited</strong>
            </p>
            <p className="privacy-body" style={{ marginBottom: '6px' }}>
              UK Mall, Ggaba Road, Kansanga, Kampala, Uganda
            </p>
            <p className="privacy-body" style={{ marginBottom: '6px' }}>
              Email:{' '}
              <a href="mailto:support@lulpay.com" className="privacy-link">
                support@lulpay.com
              </a>
            </p>
            <p className="privacy-body" style={{ marginBottom: 0 }}>
              WhatsApp:{' '}
              <a href="https://wa.me/256709413576" className="privacy-link">
                +256 709 413576
              </a>
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <LegalPageLayout
      path="/privacy"
      pageTitle={PAGE_TITLES.privacy}
      badge="🔒 Your Privacy Matters"
      title="Privacy Policy"
      lead="LulPay Remittance Limited is committed to handling your personal data responsibly and transparently."
      dates={['Effective date: 24 June 2026', 'Last updated: 24 June 2026']}
      intro={'LulPay Remittance Limited ("LulPay," "we," "us," or "our") respects your privacy and is committed to handling personal data responsibly. This Privacy Policy explains how we collect, use, disclose, protect, retain, and manage personal data when you use our website, mobile application, beta-testing environment, identity-verification processes, or official support channels.'}
      contact={{
        title: 'Have a privacy question?',
        body: 'Our team is here to help. Reach out any time.',
        cta: 'Contact Us',
        href: 'mailto:support@lulpay.com',
      }}
      sections={sections}
    />
  );
};

export default PrivacyPolicy;
